import { isDirInode } from "playground/fs/paths";
import type { HostPlayground } from "playground/hal/index";
import type { TLAssetStore } from "tldraw";
import { invariant } from "ts-invariant";

import { PROJECT_FILE_PROXY_PATH } from "@/components/ProjectFileLink";

/**
 * A sketch letters in two hands, both vendored in public/fonts.
 *
 * The "draw" slot is the architectural hand (Architects Daughter, OFL): the
 * markup voice, and what freehand and the user's own text read as.
 *
 * The "sans" slot is the sheet voice (Public Sans, a US Web Design System
 * face drawn for documents that live on a screen and on paper; OFL/CC0, see
 * PublicSans-LICENSE.md). Dims and callouts letter in it, which is why it
 * ships with the app rather than resolving to a system font: a drawing has
 * to letter identically for every peer and embed in exports, and the system
 * stack is a different face on every machine and embeds in none of them.
 *
 * Every sketch surface (pane and embed) must pass this, or tldraw falls back
 * to its stock faces.
 */
export const SKETCH_ASSET_URLS = {
  fonts: {
    tldraw_draw: "/fonts/ArchitectsDaughter.woff2",
    tldraw_draw_bold: "/fonts/ArchitectsDaughter.woff2",
    tldraw_draw_italic: "/fonts/ArchitectsDaughter.woff2",
    tldraw_draw_italic_bold: "/fonts/ArchitectsDaughter.woff2",
    // One variable file covers every weight tldraw asks for by slot.
    tldraw_sans: "/fonts/PublicSans.woff2",
    tldraw_sans_bold: "/fonts/PublicSans.woff2",
    tldraw_sans_italic: "/fonts/PublicSans.woff2",
    tldraw_sans_italic_bold: "/fonts/PublicSans.woff2",
  },
};

/**
 * Sketch assets live in the project's blob storage, not in the Automerge doc:
 * upload puts the file through the playground (S3 + a project inode under
 * assets/), and the stored `src` is the authenticated file proxy URL. Without
 * this store, tldraw inlines dropped images as base64 data URLs, which is
 * exactly the doc bloat that made the floorplan editor's underlay ephemeral.
 */
export function makeSketchAssetStore(playground: HostPlayground): TLAssetStore {
  return {
    async upload(_asset, file) {
      const finalName = await playground.upload(
        await contentAddressedName("assets", file.name, file),
        file,
        file.type || undefined,
      );
      return { src: await projectFileSrc(playground, finalName) };
    },
  };
}

/**
 * Uploaded blobs are content-addressed: the S3 store overwrites on a key
 * collision, so a re-dropped filename with different bytes would silently
 * swap the object behind every asset record (and the immutable cache would
 * hide even that). A content hash in the name makes each stored path an
 * immutable blob version; identical bytes dedup to the same key.
 */
export async function contentAddressedName(
  dir: string,
  name: string,
  data: Blob,
): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await data.arrayBuffer(),
  );
  const hash = [...new Uint8Array(digest)]
    .slice(0, 5)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  return `${dir}/${stem}-${hash}${ext}`;
}

/** The stable, doc-safe URL for a project blob file. */
export async function projectFileSrc(
  playground: HostPlayground,
  filename: string,
): Promise<string> {
  const inode = await playground.stat(filename);
  invariant(inode && !isDirInode(inode), `uploaded file ${filename} missing`);
  // stream=1: tldraw loads images crossOrigin for canvas export, which the
  // proxy's 302-to-R2 fails on CORS; a same-origin stream needs none.
  return `${PROJECT_FILE_PROXY_PATH}?source=${encodeURIComponent(inode.uri)}&stream=1`;
}
